import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableConfigDialog } from "../tables/TableConfigDialog";
import { ticketFiltersAreEqual, type TicketFilters } from "./ticketFilters";
import { FilterConfigForm } from "./FilterConfigForm";
import { ColumnConfigForm, type ColumnConfigFormProps } from "../tables/ColumnConfigForm";
import type { UseTableViewConfigResult } from "../../hooks/useTableViewConfig";

export interface TicketTableConfigDialogProps
  extends Pick<
    UseTableViewConfigResult<TicketFilters>,
    "onApplyConfig" | "activeFilters" | "isDefaultView" | "currentUserId"
  > {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClearAll?: () => void;
  title?: string;
  description?: string;
  columnConfigFormProps: Pick<
    ColumnConfigFormProps,
    "columnVisibility" | "orderedVisibleColumnIds" | "combinedFieldToColumnConfigMap" | "title"
  >;
  isDirty?: boolean;
}

export const TicketTableConfigDialog = ({
  onApplyConfig,
  activeFilters,
  isDefaultView,
  currentUserId,
  onClearAll,
  title,
  description,
  columnConfigFormProps: {
    columnVisibility,
    orderedVisibleColumnIds,
    combinedFieldToColumnConfigMap,
    title: columnConfigTitle,
  },
  isDirty: providedIsDirty,
  ...rest
}: TicketTableConfigDialogProps) => {
  const { t } = useTranslation("tickets");

  const [stagedColumnIds, setStagedColumnIds] = useState<string[]>(orderedVisibleColumnIds);
  const [stagedFilters, setStagedFilters] = useState<TicketFilters>(activeFilters);

  const isColumnsDirty = useMemo(() => {
    if (stagedColumnIds.length !== orderedVisibleColumnIds.length) return true;
    return stagedColumnIds.some((id, i) => id !== orderedVisibleColumnIds[i]);
  }, [stagedColumnIds, orderedVisibleColumnIds]);

  const isFiltersDirty = useMemo(
    () => !ticketFiltersAreEqual(stagedFilters, activeFilters),
    [stagedFilters, activeFilters]
  );

  const isDirty = providedIsDirty || isColumnsDirty || isFiltersDirty;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setStagedColumnIds(orderedVisibleColumnIds);
      setStagedFilters(activeFilters);
    }
    rest.onOpenChange?.(open);
  };

  const handleApply = () => {
    onApplyConfig(stagedColumnIds, stagedFilters);
  };

  return (
    <TableConfigDialog
      {...rest}
      onOpenChange={handleOpenChange}
      onClearAll={onClearAll}
      title={title ?? t("Configure Ticket Table View")}
      description={description ?? t("Add and remove columns, and apply filters to narrow down the tickets")}
      isDirty={isDirty}
      onApply={handleApply}
    >
      <ColumnConfigForm
        columnVisibility={columnVisibility}
        orderedVisibleColumnIds={orderedVisibleColumnIds}
        combinedFieldToColumnConfigMap={combinedFieldToColumnConfigMap}
        stagedColumnIds={stagedColumnIds}
        onStagedColumnIdsChange={setStagedColumnIds}
        title={columnConfigTitle}
        isDirty={isColumnsDirty}
        reset={() => setStagedColumnIds(orderedVisibleColumnIds)}
      />
      <FilterConfigForm
        stagedFilters={stagedFilters}
        onStagedFiltersChange={setStagedFilters}
        isDefaultView={isDefaultView}
        currentUserId={currentUserId}
      />
    </TableConfigDialog>
  );
};
